// pulse_app/src/hooks/useConfig.js


import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import configService from '../services/configService';

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const [configurations, setConfigurations] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch configurations from Storage
  const fetchConfigurations = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await configService.getAll(filters);
      setConfigurations(response.configurations || {});
      setLastUpdated(new Date().toISOString());
      
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch  configurations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update configuration
  const updateConfiguration = useCallback(async (key, value, metadata = {}) => {
    try {
      await configService.update(key, value, metadata);
      
      // Update local state
      setConfigurations(prev => ({
        ...prev,
        [key]: {
          value,
          metadata,
          updated_at: new Date().toISOString()
        }
      }));
      
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to update configuration:', err);
      return false;
    }
  }, []);

  // Create configuration
  const createConfiguration = useCallback(async (key, value, metadata = {}) => {
    try {
      await configService.create(key, value, metadata);
      
      // Update local state
      setConfigurations(prev => ({
        ...prev,
        [key]: {
          value,
          metadata,
          updated_at: new Date().toISOString()
        }
      }));
      
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to create configuration:', err);
      return false;
    }
  }, []);

  // Delete configuration
  const deleteConfiguration = useCallback(async (key) => {
    try {
      await configService.delete(key);
      
      // Update local state
      setConfigurations(prev => {
        const newConfigs = { ...prev };
        delete newConfigs[key];
        return newConfigs;
      });
      
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Failed to delete configuration:', err);
      return false;
    }
  }, []);

  // Get tooltip text (for UI integration)
  const getTooltip = useCallback((tooltipKey, defaultText = '') => {
    const key = tooltipKey.startsWith('tooltip_') ? tooltipKey : `tooltip_${tooltipKey}`;
    return configurations[key]?.value || defaultText;
  }, [configurations]);

  // Get configuration value
  const getConfig = useCallback((key, defaultValue = null) => {
    return configurations[key]?.value !== undefined ? configurations[key].value : defaultValue;
  }, [configurations]);

  // Force refresh (like your sales data refresh)
  const refresh = useCallback(async () => {
    console.log('🔄 Force refresh configurations');
    setRefreshing(true);
    try {
      await fetchConfigurations();
    } finally {
      setRefreshing(false);
    }
  }, [fetchConfigurations]);

  // Backup configurations
  const backupConfigurations = useCallback(async () => {
    try {
      const result = await configService.backup();
      return result;
    } catch (err) {
      setError(err.message);
      console.error('Failed to backup configurations:', err);
      throw err;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const value = {
    configurations,
    loading,
    refreshing,
    error,
    lastUpdated,
    getConfig,
    getTooltip,
    updateConfiguration,
    createConfiguration,
    deleteConfiguration,
    fetchConfigurations,
    refresh,
    backupConfigurations,
    
    // Computed values
    isEmpty: Object.keys(configurations).length === 0,
    hasData: Object.keys(configurations).length > 0,
    configCount: Object.keys(configurations).length
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (key = null, defaultValue = null) => {
  const context = useContext(ConfigContext);
  
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  
  if (key) {
    return {
      ...context,
      value: context.getConfig(key, defaultValue)
    };
  }
  
  return context;
};